import { showDeleteConfirmation } from '@modules/common';
import { switchMap, tap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { AutomationWebhookAPI } from '@business/api/automation_webhook';
import { AutomationWebhookId, AutomationWebhookResponse } from '@business/entities/automation_webhook';
import { AutomationAPI } from '@business/api/automation';
import { AutomationTrigger } from '@business/entities/automation';
import { BehaviorSubject } from 'rxjs';

export class AutomationWebhookBloc extends BaseBloc {
  private readonly automationWebhookRepo = new Repository<AutomationWebhookResponse>({
    getItemId: webhook => webhook.id,
  });

  readonly automationWebhooks$ = this.automationWebhookRepo.items$;
  readonly triggers$ = new BehaviorSubject<AutomationTrigger[]>([]);

  onReset() {
    this.automationWebhookRepo.reset();
  }

  fetchTriggers = () => {
    return AutomationAPI.fetchTriggers().pipe(
      tap(triggers => this.triggers$.next(triggers)),
      handleMessage({ error: requestMessage('fetch_automation_webhook_error') }),
    );
  };

  fetchAutomationWebhooks = () => {
    return AutomationWebhookAPI.fetchAutomationWebhooks().pipe(
      this.automationWebhookRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_automation_webhook_error') }),
    );
  };

  createAutomationWebhook = (webhook: Partial<AutomationWebhookResponse>) => {
    return AutomationWebhookAPI.createAutomationWebhook(webhook).pipe(
      this.automationWebhookRepo.ops.addOne(item => item),
      handleMessage({ type: requestMessage('create_automation_webhook') }),
    );
  };

  updateAutomationWebhook = (id: AutomationWebhookId, webhook: Partial<AutomationWebhookResponse>) => {
    return AutomationWebhookAPI.updateAutomationWebhook(id, webhook).pipe(
      this.automationWebhookRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ type: requestMessage('update_automation_webhook') }),
    );
  };

  deleteAutomationWebhook = (id: AutomationWebhookId) => {
    return showDeleteConfirmation('Delete Automation Webhook', 'Do you really want to remove this automation webhook?').pipe(
      switchMap(() => AutomationWebhookAPI.deleteAutomationWebhook(id)),
      this.automationWebhookRepo.ops.removeOne(() => id),
      handleMessage({ type: requestMessage('delete_automation_webhook') }),
    );
  };
}
